import React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { getSessionUser } from '../../actions/getSessionUser';
import { gql } from 'shared/__generated__/gql';
import { CheckboxWithBadge } from '../../components/ui/badges';
import { useThemeColors } from 'shared/misc/hooks/useThemeColors';
import {
  BADGE_LEVEL,
  BADGE_NAMES,
  badgesList,
  totalBadgesCount,
  calculateTotalCompletebadges,
} from 'shared/badges';

const BADGES_PAGE_QUERY = gql(/* GraphQL */ `
  query BadgesPageQuery {
    me {
      badges
    }
  }
`);

const RadialProgress = ({
  value,
  total,
  radius,
}: {
  value: number;
  total: number;
  radius: number;
}) => {
  const percentage = Math.round((value / total) * 100);
  const stroke = 5;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const theme = useThemeColors();

  return (
    <div className=" relative ">
      <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
        <circle
          stroke={theme.main}
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={theme.highlight}
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset }}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      <div
        className={`absolute inset-0 flex items-center justify-center text-brand-highlight typography-action-sm text-[${
          radius - 5
        }px] `}
      >
        {percentage}%
      </div>
    </div>
  );
};

export const Route = createFileRoute('/badges/')({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: 'PI.FYI | BADGES',
      },
    ],
  }),
  beforeLoad: async () => {
    try {
      const user = await getSessionUser();
      if (!user) {
        throw redirect({ to: '/welcome' });
      }
    } catch (error) {
      console.error(error);
    }
    return;
  },
  // fetch users badges in the loader
  loader: async ({ context }) => {
    const result = await context.apolloClient.query({
      query: BADGES_PAGE_QUERY,
    });
    return result;
  },
});

function RouteComponent() {
  const result = Route.useLoaderData();
  const {
    data: { me },
  } = result;

  type badgesType = { [key in BADGE_NAMES]: BADGE_LEVEL | boolean };
  const badges: badgesType | null = (me?.badges as unknown as badgesType) || null;
  const totalEarnedBadgesCount = calculateTotalCompletebadges(badges);

  return (
    <div className="flex flex-col justify-center mb-100 p-[12px] pt-[48px]">
      <div className="flex flex-col gap-[12px] justify-center w-full items-center">
        <h1 className="bg-brand-background px-[12px] py-[12px] uppercase typography-heading-xl text-brand-highlight text-center">
          MY BADGES
        </h1>
        <div className="flex flex-row items-center gap-6 p-[12px] typography-heading-lg bg-brand-background">
          <RadialProgress value={totalEarnedBadgesCount} total={totalBadgesCount} radius={25} />
          <span className="text-brand-highlight text-2xl font-bold">
            You have {totalBadgesCount - totalEarnedBadgesCount} badges left to unlock!
          </span>
        </div>
        <div className="grid lg:grid-cols-4 md:grid-cols-3 grid-cols-2 p-[12px] gap-[6px] justify-center  items-center">
          {Object.entries(badgesList).map(([key, badge]) => (
            <React.Fragment key={key}>
              {badge.type === 'boolean' ? (
                <CheckboxWithBadge
                  key={key}
                  badge={key as BADGE_NAMES}
                  level={false}
                  checked={!!badges?.[key as BADGE_NAMES]}
                />
              ) : (
                <>
                  <CheckboxWithBadge
                    checked={
                      badges?.[key as BADGE_NAMES] &&
                      badges?.[key as BADGE_NAMES] !== BADGE_LEVEL.UNEARNED
                    }
                    badge={key as BADGE_NAMES}
                    level={BADGE_LEVEL.BRONZE}
                  />
                  <CheckboxWithBadge
                    badge={key as BADGE_NAMES}
                    level={BADGE_LEVEL.SILVER}
                    checked={
                      badges?.[key as BADGE_NAMES] === BADGE_LEVEL.SILVER ||
                      badges?.[key as BADGE_NAMES] === BADGE_LEVEL.GOLD
                    }
                  />
                  <CheckboxWithBadge
                    badge={key as BADGE_NAMES}
                    checked={badges?.[key as BADGE_NAMES] === BADGE_LEVEL.GOLD}
                    level={BADGE_LEVEL.GOLD}
                  />
                </>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
